import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../apiServices/axios';
import { IRL_DOC, SAS_API, DATAROOM_SHARE, DATAROOM_SHARED_RESOLVE, irlDocIndexStatus, irlDocRetryIndex } from '../apiServices/api_routes';
import { toast } from 'react-toastify';
import { isRagIndexableFileName } from '../utils/vdrUploadUtils';
import {
    VDR_INDEXING_MAX_AGE_MS,
    INDEXING_FAILED_MESSAGE,
    classifyAzureIndexStatus,
    emptyUploadTray,
    expireStaleTrayItems,
    pruneExpiredIndexingEntries,
    readIndexingStorage,
    writeIndexingStorage,
} from '../utils/vdrIndexingQueue';

export { VDR_INDEXING_MAX_AGE_MS };

const checkStatus = Data => {
    if (Data?.length === 0) {
        return 'pending';
    }
    return 'completed';
};

const normalizeUploadedDocs = payload => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (payload?.document_link || payload?.id) return [payload];
    return [];
};

const isRagIndexableUpload = doc => {
    if (!doc?.id || doc?.document_link) return false;
    return isRagIndexableFileName(doc?.document_name || doc?.document);
};

const collectIndexableUploads = (docs, wsId, projectId) => {
    const pid = String(projectId || localStorage.getItem('project_id') || '');
    const wid = wsId != null ? Number(wsId) : null;
    if (!pid || !wid) return [];
    const now = Date.now();
    return (docs || [])
        .filter(isRagIndexableUpload)
        .map(doc => ({
            id: Number(doc.id),
            wsId: wid,
            projectId: pid,
            startedAt: now,
            status: 'indexing',
        }));
};

const persistIndexingFiles = state => {
    writeIndexingStorage(pruneExpiredIndexingEntries(state.indexingFiles));
};

const indexingQueueSignature = files =>
    (files || [])
        .map(item => `${item?.id}:${item?.status || 'indexing'}:${item?.startedAt || 0}`)
        .join('|');

const pruneEphemeralIndexingState = state => {
    const now = Date.now();
    const prevFiles = state.indexingFiles || [];
    const nextFiles = pruneExpiredIndexingEntries(prevFiles, now);
    if (indexingQueueSignature(nextFiles) !== indexingQueueSignature(prevFiles)) {
        state.indexingFiles = nextFiles;
        persistIndexingFiles(state);
    }
    const tray = ensureUploadTray(state);
    const nextTray = expireStaleTrayItems(tray.items, now);
    if (nextTray.some((item, i) => item.status !== tray.items[i]?.status)) {
        tray.items = nextTray;
    }
};

const ensureUploadTray = state => {
    if (!state.uploadTray || typeof state.uploadTray !== 'object') {
        state.uploadTray = emptyUploadTray();
    }
    if (!Array.isArray(state.uploadTray.items)) {
        state.uploadTray.items = [];
    }
    return state.uploadTray;
};

const markTrayItemsReadyByDocIds = (state, docIds) => {
    const doneSet = new Set((docIds || []).map(id => String(id)));
    if (!doneSet.size) return;
    const tray = ensureUploadTray(state);
    if (!tray.items.length) return;
    tray.items = tray.items.map(item => {
        const docKey = item.documentId != null ? String(item.documentId) : '';
        if (
            docKey &&
            doneSet.has(docKey) &&
            (item.status === 'indexing' || item.status === 'uploading' || item.status === 'processing_ai')
        ) {
            return { ...item, status: 'ready', error: undefined };
        }
        return item;
    });
};

const markTrayItemsFailedByDocIds = (state, docIds) => {
    const failedSet = new Set((docIds || []).map(id => String(id)));
    if (!failedSet.size) return;
    const tray = ensureUploadTray(state);
    if (!tray.items.length) return;
    tray.items = tray.items.map(item => {
        const docKey = item.documentId != null ? String(item.documentId) : '';
        if (docKey && failedSet.has(docKey) && item.status === 'indexing') {
            return { ...item, status: 'failed', error: INDEXING_FAILED_MESSAGE };
        }
        return item;
    });
};

const markTrayItemsProcessingAiByDocIds = (state, docIds) => {
    const processingSet = new Set((docIds || []).map(id => String(id)));
    if (!processingSet.size) return;
    const tray = ensureUploadTray(state);
    if (!tray.items.length) return;
    tray.items = tray.items.map(item => {
        const docKey = item.documentId != null ? String(item.documentId) : '';
        if (
            docKey &&
            processingSet.has(docKey) &&
            (item.status === 'indexing' || item.status === 'uploading')
        ) {
            return { ...item, status: 'processing_ai', error: undefined };
        }
        return item;
    });
};

const markTrayItemsIndexingByDocIds = (state, docIds) => {
    const retrySet = new Set((docIds || []).map(id => String(id)));
    if (!retrySet.size) return;
    const tray = ensureUploadTray(state);
    if (!tray.items.length) return;
    const now = Date.now();
    tray.items = tray.items.map(item => {
        const docKey = item.documentId != null ? String(item.documentId) : '';
        if (docKey && retrySet.has(docKey) && item.status === 'failed') {
            return { ...item, status: 'indexing', error: undefined, startedAt: now };
        }
        return item;
    });
};

const applyMarkFilesIndexing = (state, payload) => {
    const incoming = (payload || []).filter(item => item?.id && item?.wsId && item?.projectId);
    if (!incoming.length) return;
    const now = Date.now();
    pruneEphemeralIndexingState(state);
    const byId = new Map((state.indexingFiles || []).map(item => [Number(item.id), item]));
    incoming.forEach(item => {
        const id = Number(item.id);
        byId.set(id, {
            id,
            wsId: Number(item.wsId),
            projectId: String(item.projectId),
            startedAt: Number(item.startedAt) || now,
            status: 'indexing',
        });
    });
    state.indexingFiles = [...byId.values()];
    persistIndexingFiles(state);
    markTrayItemsIndexingByDocIds(
        state,
        incoming.map(item => item.id),
    );
};

const applyMarkFilesIndexingFailed = (state, payload) => {
    const failedIds = [...new Set((payload || []).map(Number).filter(Number.isFinite))];
    if (!failedIds.length) return;
    const byId = new Map((state.indexingFiles || []).map(item => [Number(item.id), item]));
    const trayItems = state.uploadTray?.items || [];
    const projectId = String(localStorage.getItem('project_id') || '');
    failedIds.forEach(id => {
        const existing = byId.get(id);
        if (existing) {
            byId.set(id, { ...existing, status: 'failed' });
            return;
        }
        const trayItem = trayItems.find(item => Number(item.documentId) === id);
        if (trayItem?.wsId == null || !projectId) return;
        byId.set(id, {
            id,
            wsId: Number(trayItem.wsId),
            projectId,
            startedAt: Number(trayItem.startedAt) || 0,
            status: 'failed',
        });
    });
    state.indexingFiles = [...byId.values()];
    persistIndexingFiles(state);
    markTrayItemsFailedByDocIds(state, failedIds);
};

export const GetIRLDoc = createAsyncThunk('IRLDOC/getIRLDoc', async id => id && (await axiosInstance.get(`${IRL_DOC}${localStorage['project_id']}/${id}`)));
export const GetPopulateDownloadBy = createAsyncThunk(
    'IRLDOC/GetPopulateDownloadBy',
    async ({ id, wsId }) => await axiosInstance.get(`${IRL_DOC}${localStorage['project_id']}/${wsId}/${id}/download`),
);

export const GetIRLDocumentDownloadUrl = createAsyncThunk(
    'IRLDOC/getIRLDocumentDownloadUrl',
    async ({ id, wsId }, { rejectWithValue }) => {
        try {
            const projectId = localStorage.getItem('project_id');
            const { data } = await axiosInstance.get(`${IRL_DOC}${projectId}/${wsId}/${id}/download/`);
            if (!data?.url) {
                return rejectWithValue(data || { detail: 'Download not available' });
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const GetSingleIRLDoc = createAsyncThunk('IRLDOC/getSingleIRLDoc', async ({ wsId, docId }) => {
    const data = await axiosInstance.get(IRL_DOC + localStorage['project_id'] + '/' + wsId + '/' + docId);
    return data;
});
export const GetIRLDocData = createAsyncThunk('IRLDOC/getIRLDocData', async ({ id, workstream }) => {
    const data = await axiosInstance.get(IRL_DOC + localStorage['project_id'] + '/' + workstream + '/' + id + '/');
    return data;
});

export const GetDocumentShareLink = createAsyncThunk(
    'IRLDOC/getDocumentShareLink',
    async (documentId, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get(DATAROOM_SHARE, {
                params: { document_id: documentId },
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const CreateDocumentShareLink = createAsyncThunk(
    'IRLDOC/createDocumentShareLink',
    async (documentId, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post(DATAROOM_SHARE, {
                document_id: documentId,
            });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const ResolveDocumentShareLink = createAsyncThunk(
    'IRLDOC/resolveDocumentShareLink',
    async (shareId, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get(`${DATAROOM_SHARED_RESOLVE}${shareId}/`);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const GetAuthenticatedDocUrl = createAsyncThunk('IRLDOC/getAuthenticatedDocUrl', async (docUrl, { rejectWithValue }) => {
    try {
        if (!docUrl) {
            return rejectWithValue({ detail: 'Missing document URL' });
        }
        const isAzureBlob = String(docUrl).includes('.blob.core.windows.net');
        if (!isAzureBlob) {
            return docUrl;
        }
        const response = await axiosInstance.post(SAS_API, null, {
            params: { ProjectImg: docUrl },
        });
        const sasUrl = response.data?.image;
        if (!sasUrl || !String(sasUrl).includes('sig=')) {
            return rejectWithValue({ detail: 'Could not authorize file download' });
        }
        return sasUrl;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PostIRLDocuments = createAsyncThunk('POST_IRL_DOC', async ({ body, ws_id }, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.post(IRL_DOC + localStorage['project_id'] + '/' + ws_id + '/', body);
        return data;
    } catch (error) {
        const data = error.response?.data;
        // Never pass Django DEBUG HTML (or other giant payloads) into the UI.
        if (typeof data === 'string' && (/<!DOCTYPE\s+html/i.test(data) || /<html[\s>]/i.test(data) || data.length > 500)) {
            return rejectWithValue({ detail: 'Upload failed. Please try again.' });
        }
        return rejectWithValue(data || { detail: 'Upload failed. Please try again.' });
    }
});

export const PostFolderDocuments = createAsyncThunk('POST_FOLDER_DOC', async ({ body, ws_id }, { rejectWithValue }) => {
    try {
        const projectId = localStorage.getItem('project_id');
        const url = `${IRL_DOC}${projectId}/${ws_id}/bulk_folder_upload/`;
        const response = await axiosInstance.post(url, body);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response.data);
    }
});

const INDEX_STATUS_BATCH_SIZE = 100;

export const GetDocumentIndexStatus = createAsyncThunk(
    'IRLDOC/getDocumentIndexStatus',
    async ({ wsId, fileIds, projectId }, { rejectWithValue }) => {
        try {
            const pid = projectId || localStorage.getItem('project_id');
            const ids = [...new Set((fileIds || []).map(Number).filter(Number.isFinite))];
            if (!ids.length) {
                return { statuses: [] };
            }

            const allStatuses = [];
            for (let offset = 0; offset < ids.length; offset += INDEX_STATUS_BATCH_SIZE) {
                const chunk = ids.slice(offset, offset + INDEX_STATUS_BATCH_SIZE);
                const { data } = await axiosInstance.get(irlDocIndexStatus(pid, wsId, chunk));
                const statuses = data?.statuses || data?.data?.statuses || [];
                allStatuses.push(...statuses);
            }
            return { statuses: allStatuses };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const RetryDocumentIndex = createAsyncThunk(
    'IRLDOC/retryDocumentIndex',
    async ({ wsId, fileIds, projectId }, { rejectWithValue }) => {
        try {
            const pid = projectId || localStorage.getItem('project_id');
            const ids = [...new Set((fileIds || []).map(Number).filter(Number.isFinite))];
            if (!ids.length) {
                return { retried: [], skipped: [], wsId, fileIds: ids, projectId: pid };
            }
            const { data } = await axiosInstance.post(irlDocRetryIndex(pid, wsId), { file_ids: ids });
            return { ...data, wsId, fileIds: ids, projectId: pid };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const PatchRLDocuments = createAsyncThunk('PATCH_IRL_DOC', async body => {
    const data = await axiosInstance.patch(IRL_DOC + body?.id + '/?project_id=' + localStorage['project_id'], body);
    return data;
});
export const DeleteBulkFiles = createAsyncThunk('DELETE_BULK_IRL_DOC', async ({ ws_id, items }, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.post(IRL_DOC + localStorage['project_id'] + '/' + ws_id + '/bulk_delete/', { items });
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const documentsSlice = createSlice({
    name: 'documentsSlice',
    initialState: {
        loading: true,
        data: [],
        status: '',
        errorMessage: '',
        error: false,
        docCounts: null,
        bulkFilesLoading: false,
        /** Recently uploaded files still waiting on Azure RAG indexer. */
        indexingFiles: [],
        /** Option B — floating multi-file upload progress tray. */
        uploadTray: emptyUploadTray(),
    },
    reducers: {
        setDocs: (state, { payload }) => {
            return { ...state, status: checkStatus(payload), data: payload };
        },
        resetDocumentData: (state, { payload }) => {
            return {
                loading: true,
                data: [],
                status: '',
                errorMessage: '',
                error: false,
                docCounts: null,
                bulkFilesLoading: false,
                indexingFiles: [],
                uploadTray: emptyUploadTray(),
            };
        },
        addDoc: (state, { payload }) => {
            return {
                ...state,
                status: checkStatus([payload]),
                data: [...state.data, payload],
            };
        },
        hydrateIndexingFiles: state => {
            const now = Date.now();
            const fromDisk = pruneExpiredIndexingEntries(readIndexingStorage(), now);
            const fromMemory = pruneExpiredIndexingEntries(state.indexingFiles, now);
            const byId = new Map();
            [...fromDisk, ...fromMemory].forEach(item => byId.set(Number(item.id), item));
            state.indexingFiles = [...byId.values()];
            const tray = ensureUploadTray(state);
            tray.items = expireStaleTrayItems(tray.items, now);
            persistIndexingFiles(state);
        },
        pruneExpiredIndexingState: state => {
            pruneEphemeralIndexingState(state);
        },
        markFilesIndexing: (state, { payload }) => {
            applyMarkFilesIndexing(state, payload);
        },
        markFilesIndexed: (state, { payload }) => {
            const doneIds = new Set((payload || []).map(Number));
            if (!doneIds.size) return;
            state.indexingFiles = state.indexingFiles.filter(item => !doneIds.has(Number(item.id)));
            persistIndexingFiles(state);
            markTrayItemsReadyByDocIds(state, [...doneIds]);
        },
        markFilesIndexingFailed: (state, { payload }) => {
            applyMarkFilesIndexingFailed(state, payload);
        },
        startUploadBatch: (state, { payload }) => {
            const tray = ensureUploadTray(state);
            const incoming = payload?.items || [];
            tray.destinationLabel = payload?.destinationLabel || tray.destinationLabel || '';
            tray.collapsed = false;
            tray.items = [...tray.items, ...incoming];
        },
        updateUploadTrayItem: (state, { payload }) => {
            const tray = ensureUploadTray(state);
            const { clientId, ...rest } = payload || {};
            if (!clientId) return;
            tray.items = tray.items.map(item => {
                if (item.clientId !== clientId) return item;
                const next = { ...item, ...rest };
                if (
                    (next.status === 'indexing' || next.status === 'uploading') &&
                    !(Number(next.startedAt) > 0)
                ) {
                    next.startedAt = Date.now();
                }
                return next;
            });
        },
        removeUploadTrayItem: (state, { payload: clientId }) => {
            const tray = ensureUploadTray(state);
            tray.items = tray.items.filter(item => item.clientId !== clientId);
            if (!tray.items.length) {
                state.uploadTray = emptyUploadTray();
            }
        },
        clearFinishedUploadTrayItems: state => {
            const tray = ensureUploadTray(state);
            tray.items = tray.items.filter(item => !['ready', 'skipped'].includes(item.status));
            if (!tray.items.length) {
                state.uploadTray = emptyUploadTray();
            }
        },
        setUploadTrayCollapsed: (state, { payload }) => {
            ensureUploadTray(state).collapsed = Boolean(payload);
        },
        dismissUploadTray: state => {
            state.uploadTray = emptyUploadTray();
        },
    },
    extraReducers: {
        [GetIRLDoc.fulfilled]: (state, { payload }) => {
            state.data = payload?.data?.documents || [];
            state.docCounts = payload?.data?.doc_count;
            state.status = checkStatus(payload?.data);
            state.loading = false;
        },
        [GetIRLDoc.rejected]: (state, action) => {
            state.error = true;
            state.errorMessage = action?.error?.message;
            state.data = [];
            state.loading = false;
        },

        //GetIRLDocData
        [GetIRLDocData.fulfilled]: (state, { payload }) => {
            state.data = state.data?.map(item => {
                if (item?.id === payload?.data?.id) return payload?.data;
                else return item;
            });
        },
        [GetIRLDocData.rejected]: (state, action) => {
            console.log({ action });
        },

        // PostIRLDocuments
        [PostIRLDocuments.fulfilled]: (state, { payload, meta }) => {
            state.loading = false;
            let data = payload?.data?.document_link ? [payload.data] : payload?.data;
            if (state?.data && state?.data?.length > 0) state.data = [...state.data, ...data];
            else state.data = data;

            const uploads = collectIndexableUploads(
                normalizeUploadedDocs(data),
                meta?.arg?.ws_id,
                localStorage.getItem('project_id'),
            );
            if (uploads.length) {
                const existingIds = new Set(state.indexingFiles.map(item => Number(item.id)));
                uploads.forEach(item => {
                    if (!existingIds.has(item.id)) {
                        state.indexingFiles.push({ ...item, status: item.status || 'indexing' });
                        existingIds.add(item.id);
                    }
                });
                persistIndexingFiles(state);
            }
        },
        [PostIRLDocuments.rejected]: (state, { payload }) => {
            // Object.values(payload).map(value => {
            //     value?.forEach(msg => toast.error(msg));
            // });
            state.error = true;
        },
        // PatchRLDocuments
        [PatchRLDocuments.fulfilled]: (state, { payload }) => {
            state.loading = false;
            state.data = state?.data?.map(item => {
                if (item?.id === payload?.data?.id) return payload?.data;
                else return item;
            });
        },
        [PatchRLDocuments.rejected]: (state, action) => {
            state.error = true;
        },
        //DeleteBulkFiles
        [DeleteBulkFiles.pending]: (state, action) => {
            state.status = 'loading';
            state.bulkFilesLoading = true;
        },
        [DeleteBulkFiles.fulfilled]: (state, action) => {
            console.log(action);
            const ids = action?.meta?.arg?.items;
            state.data = state.data.filter(item => {
                let check = ids.find(id => Number(id) === item?.id);
                if (!check) return item;
            });
            const deleted = new Set((ids || []).map(Number));
            state.indexingFiles = state.indexingFiles.filter(item => !deleted.has(Number(item.id)));
            persistIndexingFiles(state);
            state.status = 'success';
            state.bulkFilesLoading = false;
            toast.success('Successfully Deleted');
        },
        [DeleteBulkFiles.rejected]: (state, action) => {
            state.status = 'rejected';
            state.bulkFilesLoading = false;
            console.log({ action });
            const errorMessage = action?.payload?.detail || action?.payload?.message || action?.error?.message || 'Failed to delete documents';
            toast.error(errorMessage);
        },
        // GetPopulateDownloadBy
        [GetPopulateDownloadBy.fulfilled]: (state, action) => {
            console.log({ action });
        },
        // PostFolderDocuments
        [PostFolderDocuments.pending]: (state, action) => {
            state.bulkFilesLoading = true;
        },
        [PostFolderDocuments.fulfilled]: (state, action) => {
            state.bulkFilesLoading = false;
            if (state.data?.length > 0) {
                state.data = [...state.data, ...action?.payload];
            } else {
                state.data = action.payload;
            }
            const uploads = collectIndexableUploads(
                normalizeUploadedDocs(action?.payload),
                action?.meta?.arg?.ws_id,
                localStorage.getItem('project_id'),
            );
            if (uploads.length) {
                const existingIds = new Set(state.indexingFiles.map(item => Number(item.id)));
                uploads.forEach(item => {
                    if (!existingIds.has(item.id)) {
                        state.indexingFiles.push({ ...item, status: item.status || 'indexing' });
                        existingIds.add(item.id);
                    }
                });
                persistIndexingFiles(state);
            }
        },
        [PostFolderDocuments.rejected]: (state, action) => {
            state.bulkFilesLoading = false;
            // Check if it's a duplicate folder error
            if (action?.payload?.non_field_errors && action.payload.non_field_errors.some(error => error.includes('The fields workstream, name must make a unique set'))) {
                toast.error('Folder not added - a folder with this name already exists in the workstream.');
            } else {
                toast.error('Failed to upload folder');
            }
        },
        [GetDocumentIndexStatus.fulfilled]: (state, { payload }) => {
            const statuses = payload?.statuses || payload?.data?.statuses || [];
            if (!statuses.length) return;

            const now = Date.now();
            const entryById = new Map(
                (state.indexingFiles || []).map(item => [Number(item.id), item]),
            );
            const trayByDocId = new Map();
            (state.uploadTray?.items || []).forEach(item => {
                if (item?.documentId == null) return;
                trayByDocId.set(Number(item.documentId), item);
            });

            const doneIds = [];
            const failedIds = [];
            const processingAiIds = [];
            statuses.forEach(row => {
                const id = Number(row?.file_id);
                if (!Number.isFinite(id)) return;
                const startedAt =
                    Number(entryById.get(id)?.startedAt) ||
                    Number(trayByDocId.get(id)?.startedAt) ||
                    0;
                const next = classifyAzureIndexStatus(row, { startedAt, now });
                if (next === 'done') doneIds.push(id);
                else if (next === 'failed') failedIds.push(id);
                else if (next === 'processing_ai') processingAiIds.push(id);
            });

            if (processingAiIds.length) {
                markTrayItemsProcessingAiByDocIds(state, processingAiIds);
            }
            if (doneIds.length) {
                const doneSet = new Set(doneIds);
                state.indexingFiles = (state.indexingFiles || []).filter(
                    item => !doneSet.has(Number(item.id)),
                );
                persistIndexingFiles(state);
                markTrayItemsReadyByDocIds(state, doneIds);
            }
            if (failedIds.length) {
                applyMarkFilesIndexingFailed(state, failedIds);
            }
        },
        [RetryDocumentIndex.pending]: (state, { meta }) => {
            const ids = meta?.arg?.fileIds || [];
            const wsId = meta?.arg?.wsId;
            const projectId = meta?.arg?.projectId || localStorage.getItem('project_id');
            applyMarkFilesIndexing(
                state,
                ids.map(id => ({ id, wsId, projectId })),
            );
        },
        [RetryDocumentIndex.rejected]: (state, { meta, payload }) => {
            const ids = meta?.arg?.fileIds || [];
            applyMarkFilesIndexingFailed(state, ids);
            const errorMessage =
                payload?.detail || payload?.message || (typeof payload === 'string' ? payload : null) ||
                'Could not retry indexing';
            toast.error(errorMessage);
        },
    },
});

export const {
    setDocs,
    addDoc,
    resetDocumentData,
    hydrateIndexingFiles,
    pruneExpiredIndexingState,
    markFilesIndexing,
    markFilesIndexed,
    markFilesIndexingFailed,
    startUploadBatch,
    updateUploadTrayItem,
    removeUploadTrayItem,
    clearFinishedUploadTrayItems,
    setUploadTrayCollapsed,
    dismissUploadTray,
} = documentsSlice.actions;
export default documentsSlice.reducer;
